import { DatePipe, TitleCasePipe } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {
  CalendarEvent,
  CalendarMonthViewDay,
  CalendarView
} from "angular-calendar";
import { format, lastDayOfMonth, lastDayOfWeek, startOfWeek } from "date-fns";
import { asyncScheduler, Observable, Subject, Subscription } from "rxjs";
import { AppState } from "../../core";
import { activity_status } from "../../core/models/task-status";
import { DriveTestDoc } from "../../core/schema/drive-test-schema";
import { PathTestDoc } from "../../core/schema/path-test-schema";
import { ScoutTaskDoc } from "../../core/schema/scout-task-schema";
import { FesBaseComponent } from "../../shared/components/fes-base.component";
import { FilterFieldMode } from "../../shared/directives/filter-field.directive";
import { TaskStatusCssPipe } from "../../shared/pipes/task-status.pipe";
import { TaskRow } from "../task-list/task-list.component";

type TaskCalendarEvent = CalendarEvent<{
  task: PathTestDoc | ScoutTaskDoc | DriveTestDoc;
}>

export type TaskCalendarDateFilter = {
  pattern: string;
  mode: FilterFieldMode;
}

@Component({
  selector: "app-task-calendar",
  templateUrl: "./task-calendar.component.html",
  styleUrls: ["./task-calendar.component.scss"],
})
export class TaskCalendarComponent
  extends FesBaseComponent
  implements OnInit, OnDestroy
{

  @Input() taskUpdated$: Subject<PathTestDoc | ScoutTaskDoc | DriveTestDoc>;

  @Input("tasks$") set _setTasks$(
    tasks$: Observable<(PathTestDoc | ScoutTaskDoc | DriveTestDoc)[]>
  ) {
    this.tasksSub?.unsubscribe();
    this.tasksSub = tasks$.subscribe((tasks) => {
      this.setTasks(tasks);
    });
  }

  @Input("tasks") set _setTasks(
    tasks: (PathTestDoc | ScoutTaskDoc | DriveTestDoc)[]
  ) {
    this.setTasks(tasks);
  }

  @Input() userNamesById: { [key: string]: string };

  @Input() buildingNamesById: { [key: string]: string };

  @Input() routeNamesByid: { [key: string]: string };

  @Input("viewMode") set _setViewMode(strMode: "day" | "week" | "month") {
    let mode: CalendarView = CalendarView.Month;
    switch (strMode) {
      case "day":
        mode = CalendarView.Day;
        break;
      case "week":
        mode = CalendarView.Week;
        break;
    }
    this.setViewMode(mode);
  }

  // Set calendar date from the task list filter text field value
  @Input("dateFilter") set _setDateFilter(filter: TaskCalendarDateFilter ) {
    //console.log(`set dateFilter(${JSON.stringify(filter)})`)
    const dateArgs =
      filter?.pattern && /(\d{4})-(\d{2})(-|$)(\d{2})?/.exec(filter.pattern);
    if (dateArgs) {
      const [_, strYear, strMonth, __, strDay] = dateArgs;
      const date = strDay
      ? new Date(parseInt(strYear), parseInt(strMonth)-1, parseInt(strDay)||1)
      : new Date(parseInt(strYear), parseInt(strMonth)-1, 1);
      if ( date != this.viewDate ) {
        this.viewDate = date
      }
    } else {
      //TBD - Should we reuse last viewDate or always reset it to current day?
      asyncScheduler.schedule(() => this.viewDateChanged(this.viewDate));
    }
  }

  @Input() viewActive = true // free some resources if tab is not active in UI

  @Input('taskKey') set _taskKeySetter( key: 'modifiedDate' | 'scheduledDate' ) {
    this.taskKey = key
    //this.viewDateChanged(this.viewDate);
  }
  taskKey : 'modifiedDate' | 'scheduledDate' = 'scheduledDate'

  @Output("dateFilterChanged") dateFilterChanged = new EventEmitter<{
    pattern: string;
    mode: FilterFieldMode;
  }>();

  @Output() editTask = new EventEmitter<
    PathTestDoc | ScoutTaskDoc | DriveTestDoc
  >();

  @Output('closeCalendar') closeCalendar$ = new EventEmitter<{clearFilter:boolean}>()

  viewMode: CalendarView = null;
  viewDate: Date = new Date();
  eventsById: { [taskId: string]: CalendarEvent } = {};
  CalendarView = CalendarView;
  tasksSub: Subscription;

  constructor(public appState: AppState, private datePipe: DatePipe) {
    super(appState);
  }


  ngOnInit(): void {
    this.subscribe(this.taskUpdated$, this.singleTaskUpdatedHandler);
    this.setViewMode(this.viewMode||CalendarView.Month);
  }


  ngOnDestroy(): void {
    this.destroy();
    this.tasksSub?.unsubscribe();
  }


  singleTaskUpdatedHandler = (
    task: PathTestDoc | ScoutTaskDoc | DriveTestDoc
  ) => {
    const strDate = this.taskKey == 'modifiedDate' ? task['row'].modifiedDate : task.schedule.scheduledDate;
    const ymdNoTzHack = (strDate ||'')
      .split("-")
      .map((str) => parseInt(str));
    const startDate = ymdNoTzHack.length > 1 ? new Date(
      ymdNoTzHack[0],
      (ymdNoTzHack[1]||1)-1,
      ymdNoTzHack[2]||1
    ) : null;
    let event: TaskCalendarEvent = {
      start: startDate,
      allDay: true,
      title: task.buildingId,
      meta: {
        task: task
      }
    };
    const row = task["row"] as TaskRow;
    const statusName = row.statusName;
    const statusId = activity_status.Id[statusName];
    const statusClass = TaskStatusCssPipe.getCssClassForId(statusId);
    const statusTitle = new TitleCasePipe().transform(statusName);
    if (task.type == "scout") {
      event.title = `<i class='fa fa-1x fa-drafting-compass text-primary mr-2'></i>${event.title}`;
    } else if (task.type == "path test") {
      event.title = `<i class='fa fa-1x fa-map-marked text-success mr-2'></i>&nbsp;${task.pathId}`;
    } else if (task.type == "drive test") {
      const driveTestDoc = task as DriveTestDoc;
      const routeId = driveTestDoc.routeId;
      event.title = `<i class='fa fa-1x fa-car-side text-info mr-2'></i>&nbsp;${routeId}`;
    }
    event.title = `<div>${event.title} (${task.stage})</div>`
    event.title += `<div class='ml-1 px-1 text-nowrap my-auto font-weight-bold ${statusClass}'>${statusTitle || ""}</div>`;
    event.title = `<div class='d-flex'>${event.title}</div>`
    this.eventsById[task._id] = event as CalendarEvent;
  };


  setTasks = (tasks: (PathTestDoc | ScoutTaskDoc | DriveTestDoc)[]) => {
    //console.log(`setTasks(tasks: len=${tasks.length})`)
    this.eventsById = {};
    tasks.forEach((t) => this.singleTaskUpdatedHandler(t));
  };


  setViewMode(viewMode: CalendarView) {
    if ( this.viewMode != viewMode ) {
      this.viewMode = viewMode;
      asyncScheduler.schedule( () => this.viewDateChanged(this.viewDate) )
    }
  }


  viewDateChanged(viewDate: Date) {
    console.log(`viewDateChanged(${viewDate})`);
    this.viewDate = viewDate;
    let strDateFilter = "";
    let strMode: FilterFieldMode = "contains";
    switch (this.viewMode) {
      case CalendarView.Day:
        strDateFilter = `${format(viewDate, "yyyy-MM-dd")}`;
        break;
      case CalendarView.Week:
        const startOfWeekDate = startOfWeek(viewDate);
        const lastDayOfMonthDate = lastDayOfMonth(startOfWeekDate);
        strDateFilter = `${format(startOfWeekDate, "yyyy-MM-")}`;
        const ifirstDayOfWeek = startOfWeekDate.getDate();
        const iLastDayOfMonth = lastDayOfMonthDate.getDate();
        //console.log(`startOfWeek=${startOfWeekDate}, firstDayOfWeek=${ifirstDayOfWeek}`)
        const days = [0, 1, 2, 3, 4, 5, 6]
          .map((i) => i + ifirstDayOfWeek)
          .filter((d) => d <= iLastDayOfMonth);
        const daysRegEx = days.map((d) => `${d}`.padStart(2, "0")).join("|");
        strDateFilter += `(${daysRegEx})`;
        if (days.length < 7) {
          const lastDayOfWeekDate = lastDayOfWeek(viewDate);
          const iLastDayOfWeek = lastDayOfWeekDate.getDate();
          strDateFilter += `|${format(lastDayOfWeekDate, "yyyy-MM-")}`;
          const days = Array.from(Array(iLastDayOfWeek).keys()).map(
            (i) => i + 1
          );
          const daysRegEx = days.map((d) => `${d}`.padStart(2, "0")).join("|");
          strDateFilter += `(${daysRegEx})`;
          strDateFilter = `^(${strDateFilter})`;
        }
        console.log(strDateFilter);
        strMode = "~*";
        break;
      default:
        strDateFilter = `${format(viewDate, "yyyy-MM")}`;
        break;
    }
    this.dateFilterChanged.emit({ pattern: strDateFilter, mode: strMode });
  }


  onCalendarEventSelected(event: CalendarEvent) {
    const taskEvent = event as TaskCalendarEvent;
    this.editTask.emit(taskEvent.meta.task);
  }


  onCalendarDaySelected(day: CalendarMonthViewDay) {
    this.viewDate = day.date;
    this.setViewMode(CalendarView.Day);
  }
}
